import { PaGrandTotal, PpAnalysisWaacDTO } from 'src/app/domain/pp/component/sub/appreciation-at-cost/dtos';
import { Rows } from '../../interfaces';
import { getCell, formats, withCellStyles } from '../../utils';
import { boldGrayBgLeft, left, leftBold } from 'src/app/util/excelStyles';
import { AppreciationAtCostExcelWrapper } from 'src/app/domain/pp/component/sub/appreciation-at-cost/appreciation-at-cost.component';

export const getPreparedData = (wrapper: AppreciationAtCostExcelWrapper, headersForLogic: string[], headersForDisplay: string[]) => {
  const data = wrapper.waacAnalysis;
  const totals = wrapper.grandTotal;
  const newData: Rows = [];
  const firstRow = getFirstRow();
  newData.push(firstRow);

  const headersForDisplayRow = [];
  headersForDisplay.forEach((header) => headersForDisplayRow.push(withCellStyles(header, leftBold)));
  newData.push(headersForDisplayRow);

  const mainRows = getMainRows(data, headersForLogic);
  newData.push(...mainRows);

  const totalRow = getTotalRow(headersForLogic, totals);
  newData.push(...totalRow);
  // set current index row to initial value
  currentRowIndex = 3;
  return newData;
};

const getMainRows = (data: PpAnalysisWaacDTO[], headersForLogic: string[]) => {
  const rows: Rows = [];
  const mappedToHeaders = data.map((waac) => getNewMapping(waac));
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of headersForLogic) {
      row.push(withCellStyles(mapped[header], left));
    }
    rows.push(row);
  }
  return rows;
};

const getTotalRow = (headersForLogic: string[],total: PaGrandTotal) => {
  const rows: Rows = [];
  const mappedToHeaders = getNewTotalMapping(total);
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of headersForLogic) {
      row.push(withCellStyles(mapped[header], boldGrayBgLeft));
    }
    rows.push(row);
  }
  return rows;
};

const getNewMapping = (waac: PpAnalysisWaacDTO) => {
  const body = {
    'Company name': waac.companyName,
    Status: waac.status,
    'Initial investment': waac.dateOfInitialInvestment,
    empty: null,

    'Investment(0)': getCell({
      v: waac.wgl.cost,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Value(0)': getCell({
      v: waac.wgl.fmv,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'MOIC(0)': getCell({
      f: `IFERROR(F${currentRowIndex}/E${currentRowIndex},0)`,
      z: formats.dashMultiplier,
      t: 'n',
    }),
    'empty(0)': null,

    'Investment(1)': getCell({
      v: waac.atCost.cost,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Value(1)': getCell({
      v: waac.atCost.fmv,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'MOIC(1)': getCell({
      f: `IFERROR(J${currentRowIndex}/I${currentRowIndex},0)`,
      z: formats.dashMultiplier,
      t: 'n',
    }),
    'empty(1)': null,

    'Investment(2)': getCell({
      f: getSumFormulaForTotalsInMainRows('I', 'E'),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Value(2)': getCell({
      f: getSumFormulaForTotalsInMainRows('J', 'F'),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'MOIC(2)': getCell({
      f: `IFERROR(N${currentRowIndex}/M${currentRowIndex},0)`,
      z: formats.dashMultiplier,
      t: 'n',
    }),
    'empty(2)': null,

    'Investment%': Math.round(waac.ratios.wglCostRatio) + '/' + Math.round(waac.ratios.atCostCostRatio),
    'Value%': Math.round(waac.ratios.wglFmvRatio) + '/' + Math.round(waac.ratios.atCostFmvRatio),
  };
  currentRowIndex++;
  return body;
};

const getNewTotalMapping = (grandTotal: PaGrandTotal) => {
  const body = {
    'Company name': 'Grand total',
    Status: '',
    'Initial investment': '',
    empty: null,
    'Investment(0)': getCell({
      f: getSummFormulaForTotal('E'),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Value(0)': getCell({
      f: getSummFormulaForTotal('F'),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'MOIC(0)': getCell({
      f: getMoicFormulaForTotal('F', 'E'),
      z: formats.multiplier,
      t: 'n',
    }),
    'empty(0)': null,
    'Investment(1)': getCell({
      f: getSummFormulaForTotal('I'),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Value(1)': getCell({
      f: getSummFormulaForTotal('J'),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'MOIC(1)': getCell({
      f: getMoicFormulaForTotal('J', 'I'),
      z: formats.multiplier,
      t: 'n',
    }),
    'empty(1)': null,
    'Investment(2)': getCell({
      f: getSumFormulaForTotalsInMainRows('I', 'E'),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Value(2)': getCell({
      f: getSumFormulaForTotalsInMainRows('J', 'F'),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'MOIC(2)': getCell({
      f: getMoicFormulaForTotal('N', 'M'),
      z: formats.dashMultiplier,
      t: 'n',
    }),
    'empty(2)': null,
    'Investment%': Math.round(grandTotal.combinedRatios.wglCostRatio) + '/' + Math.round(grandTotal.combinedRatios.atCostCostRatio),
    'Value%': Math.round(grandTotal.combinedRatios.wglFmvRatio) + '/' + Math.round(grandTotal.combinedRatios.atCostFmvRatio),

  };
  return [body];
};

// initial value of current row index it start from 3 after first row and headers
let currentRowIndex = 3;

const getFirstRow = () => [null, null, null, null,
  '', 'With gain / (loss)', '', null,
  '', 'At cost', '', null,
  '', 'Total', '', null,
  'Ratios', ''];

const getSummFormulaForTotal = (letter: string) => `SUM(${letter}3:${letter}${currentRowIndex - 1})`;

const getSumFormulaForTotalsInMainRows = (a: string, b: string) => `SUM(${a}${currentRowIndex},${b}${currentRowIndex})`;


const getMoicFormulaForTotal = (fmvLetter: string, costLetter: string) =>
  `IFERROR(${fmvLetter}${currentRowIndex}/${costLetter}${currentRowIndex},0)`;
